import { useEffect, useState } from "react";
import { CheckCircle2, Hotel, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BrandButton } from "@/components/PageHeader";
import { AccommodationInfo } from "@/components/Accommodation";
import { api, errMsg } from "@/lib/api";

const useAccommodation = (eventId, enabled) => {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ event: null, loading: true, error: "" });
  useEffect(() => {
    if (!enabled || !eventId) return;
    const controller = new AbortController();
    setState({ event: null, loading: true, error: "" });
    api.get("/events", { signal: controller.signal }).then(({ data }) => {
      const event = data.find((e) => e.id === eventId);
      if (!controller.signal.aborted) setState(event ? { event, loading: false, error: "" }
        : { event: null, loading: false, error: "This event's accommodation information is no longer available." });
    }).catch((err) => {
      if (!controller.signal.aborted) setState({ event: null, loading: false, error: `Hotel details could not be loaded. ${errMsg(err)}` });
    });
    return () => controller.abort();
  }, [eventId, enabled, attempt]);
  return { ...state, retry: () => setAttempt((v) => v + 1) };
};

const HotelDetails = ({ state, prefix }) => {
  if (state.loading) return <p role="status" className="text-sm text-slate-600 py-4" data-testid={`${prefix}-loading`}>Loading hotel details and contact numbers…</p>;
  if (state.error) return <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-900" data-testid={`${prefix}-error`}>
    <p className="text-sm">{state.error}</p>
    <BrandButton variant="outline" onClick={state.retry} className="mt-3 bg-white" data-testid={`${prefix}-retry`}><RefreshCw className="w-4 h-4" />Retry hotel details</BrandButton>
  </div>;
  return <AccommodationInfo event={state.event} prefix={prefix} />;
};

export const AccommodationDialog = ({ event, onClose }) => {
  const state = useAccommodation(event?.id, !!event);
  return <Dialog open={!!event} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-w-3xl" data-testid="slider-accommodation-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2" data-testid="slider-accommodation-title"><Hotel className="w-5 h-5 text-[#B30000]" />Accommodation options</DialogTitle>
        <DialogDescription data-testid="slider-accommodation-event">{state.event?.title || event?.title} · hotel details, rates and contact numbers</DialogDescription>
      </DialogHeader>
      <div className="max-h-[50dvh] overflow-y-auto overscroll-contain pr-1" data-testid="slider-accommodation-details"><HotelDetails state={state} prefix="slider-accommodation" /></div>
      <DialogFooter><BrandButton variant="dark" onClick={onClose} data-testid="slider-accommodation-done">Close accommodation view</BrandButton></DialogFooter>
    </DialogContent>
  </Dialog>;
};

export const RegistrationSuccessDialog = ({ receipt, event, onClose }) => {
  const showHotels = !!receipt && receipt.attendance !== "Online" && !receipt.accommodation && event?.accommodation_mode === "display";
  const state = useAccommodation(receipt?.event_id, showHotels);
  const currentCallMode = !state.event || state.event.accommodation_mode === "display";
  return <Dialog open={!!receipt} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className={showHotels && currentCallMode ? "max-w-3xl" : "max-w-lg"} data-testid="reg-success-dialog">
      <DialogHeader>
        <div className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-800 grid place-items-center mb-1" aria-hidden="true"><CheckCircle2 className="w-6 h-6" /></div>
        <DialogTitle data-testid="reg-success-title">Registration submitted successfully</DialogTitle>
        <DialogDescription data-testid="reg-success-message">{receipt?.delegate_name} is registered for {receipt?.event_title}.</DialogDescription>
      </DialogHeader>
      {receipt && <p className="text-sm text-slate-700" data-testid="reg-success-reference">Ticket <strong className="font-mono" data-testid="reg-ticket-number">{receipt.ticket_no}</strong></p>}
      {showHotels && currentCallMode && <div className="max-h-[45dvh] overflow-y-auto overscroll-contain pr-1" data-testid="reg-success-accommodation-details"><HotelDetails state={state} prefix="reg-success-accommodation" /></div>}
      <DialogFooter><BrandButton variant="dark" onClick={onClose} data-testid="reg-success-done">Done</BrandButton></DialogFooter>
    </DialogContent>
  </Dialog>;
};
